import { DateRange } from 'react-day-picker';
import { TZDate } from '@date-fns/tz';

import { Interval, TimeSpan } from '@/features/trends/trends.types';
import {
  addUTCMilliseconds,
  createUTCDate,
  getUTCNow,
  startOfFacilityDayUTC,
  subtractUTCMilliseconds,
} from '@/helpers/datetime/utc-date';

import { DeviceAttribute } from './trends.types';

/**
 * Generates a unique key for a device attribute selection.
 * This key is used to identify and track attribute selections in the UI.
 *
 * @param attr - The device attribute to generate a key for
 * @returns A unique string key in the format "selection-attribute-{deviceId}-{refId}"
 */
export const getAttributeKey = (attr: DeviceAttribute) =>
  `selection-attribute-${attr.deviceId}-${attr.displayRef.ref.id}`;

/**
 * Compares two device attributes for equality.
 * Two attributes are considered equal if they have the same device ID and reference ID.
 *
 * @param a - The first device attribute to compare
 * @param b - The second device attribute to compare
 * @returns True if the attributes are equal, false otherwise
 */
export const attributesEqual = (a: DeviceAttribute, b: DeviceAttribute) =>
  a.deviceId === b.deviceId && a.displayRef.ref.id === b.displayRef.ref.id;

/**
 * Removes duplicate device attributes from an array.
 * Uses the unique attribute key (generated by getAttributeKey) to identify duplicates.
 * When duplicates are found, only the first occurrence is kept.
 *
 * @param attrs - Array of device attributes that may contain duplicates
 * @returns A new array with duplicate attributes removed
 */
export const dedupeAttributes = (attrs: DeviceAttribute[]) => {
  const attributeMap = new Map(attrs.map((attr) => [getAttributeKey(attr), attr]));
  return Array.from(attributeMap.values());
};

/**
 * Converts a sampling interval to its equivalent duration in seconds.
 * Used to determine how frequently data points should be collected or displayed.
 *
 * @param interval - The sampling interval to convert
 * @returns The number of seconds corresponding to the interval
 */
export const mapIntervalToSeconds = (interval: Interval): number => {
  const options = {
    'per-second': 1,
    'per-15-seconds': 15,
    'per-minute': 60,
    'per-15-minutes': 900,
    'per-hour': 3600,
  };

  return options[interval];
};

/**
 * Converts a predefined time span into a concrete date range.
 * Calculates start and end dates relative to the current time for common time periods.
 * For custom date ranges (`date-range`), defaults to the start of the current day in facility time,
 * expressed as a UTC instant for BFF/query alignment.
 *
 * @param timeSpan - The time span to convert (e.g., '1hr', '24hrs', 'week', 'month', or 'date-range')
 * @returns A DateRange object with 'from' and 'to' dates representing the selected time period
 */
export const mapTimeSpanToDateRange = (timeSpan: TimeSpan): DateRange => {
  const shiftMilliseconds = getTimeSpanShift(timeSpan);

  // For predefined time spans, calculate from now
  if (shiftMilliseconds !== null) {
    return {
      from: subtractUTCMilliseconds(shiftMilliseconds),
      to: undefined,
    };
  }

  // Custom span default: facility calendar midnight as a UTC instant.
  return {
    from: startOfFacilityDayUTC(getUTCNow()),
    to: undefined,
  };
};

/**
 * Normalizes a calendar DateRange into facility-bounded Date objects.
 * Applies midnight and end-of-day boundaries relative to the provided timezone and
 * returns plain Date instances so downstream consumers remain unchanged.
 *
 * @param range - Date range emitted by the calendar component
 * @param facilityTimeZone - IANA timezone for the current facility
 * @returns Start/end Date objects or null when the range is incomplete
 */
export const getFacilityBoundedRange = (
  range: DateRange,
  facilityTimeZone: string
): { start: Date; end: Date } | null => {
  if (!range.from || !range.to) {
    return null;
  }

  const facilityStart = new TZDate(range.from, facilityTimeZone);
  const facilityEnd = new TZDate(range.to, facilityTimeZone);
  const facilityNow = new TZDate(new Date(), facilityTimeZone);

  facilityStart.setHours(0, 0, 0, 0);

  const isEndToday =
    facilityEnd.getFullYear() === facilityNow.getFullYear() &&
    facilityEnd.getMonth() === facilityNow.getMonth() &&
    facilityEnd.getDate() === facilityNow.getDate();

  const boundedEnd = new TZDate(isEndToday ? facilityNow : facilityEnd, facilityTimeZone);

  if (!isEndToday) {
    boundedEnd.setHours(23, 59, 59, 999);
  }

  return {
    start: new Date(facilityStart.getTime()),
    end: new Date(boundedEnd.getTime()),
  };
};

/**
 * Calculates the time shift amount in milliseconds for a given time span.
 * Used to navigate forward/backward through time ranges.
 *
 * @param timeSpan - The time span to calculate shift for
 * @returns The number of milliseconds to shift, or null if timespan doesn't support navigation
 */
export const getTimeSpanShift = (timeSpan: TimeSpan): number | null => {
  switch (timeSpan) {
    case '1hr':
      return 60 * 60 * 1000; // 1 hour
    case '4hrs':
      return 4 * 60 * 60 * 1000; // 4 hours
    case '12hrs':
      return 12 * 60 * 60 * 1000; // 12 hours
    case '24hrs':
      return 24 * 60 * 60 * 1000; // 24 hours
    case 'week':
      return 7 * 24 * 60 * 60 * 1000; // 7 days
    case 'month':
      return 30 * 24 * 60 * 60 * 1000; // 30 days
    case 'date-range':
      // For custom date ranges, calculate the difference between start and end
      return null;
    default:
      return null;
  }
};

/**
 * Shifts a date range forward or backward by the specified time span amount.
 * For custom date ranges, shifts by the duration between start and end.
 * For live queries (undefined end), uses the current instant in UTC as the effective end.
 *
 * @param start - The current start date
 * @param end - The current end date (undefined for live queries)
 * @param timeSpan - The time span determining shift amount
 * @param direction - Direction to shift: 'forward' or 'backward'
 * @returns New date range with shifted start and end dates
 */
export const shiftDateRange = (
  start: Date,
  end: Date | undefined,
  timeSpan: TimeSpan,
  direction: 'forward' | 'backward'
): DateRange => {
  let shiftMilliseconds = getTimeSpanShift(timeSpan);

  const effectiveEnd = createUTCDate(end);

  // For custom date ranges, use the duration between start and end
  if (shiftMilliseconds === null) {
    shiftMilliseconds = effectiveEnd.getTime() - start.getTime();
  }

  const shift = shiftMilliseconds * (direction === 'forward' ? 1 : -1);

  return {
    from: addUTCMilliseconds(shift, start),
    // For live queries going backward, return a concrete end date
    // For forward navigation that would go past "now", keep end undefined
    to:
      end === undefined && direction === 'forward'
        ? undefined
        : addUTCMilliseconds(shift, effectiveEnd),
  };
};
